import {
  Button,
  Input,
  Select,
  SpecialtySearchAutocomplete,
  User, X,
} from "@ews-admin/global-design-system";
import React from "react";
import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ProvidedServiceEnum } from "../../../../types/doctor.definition";
import { Specialty } from "../../../../types/specialty.definition";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StaffFormData = any;

interface DoctorSpecificFieldsProps {
  control: Control<StaffFormData>;
  errors?: FieldErrors<StaffFormData>;
  specialties: Specialty[];
  selectedSpecialties: Array<{ id: string; code: string; label: string }>;
  isEditing: boolean;
}

export const DoctorSpecificFields: React.FC<DoctorSpecificFieldsProps> = ({
  control,
  specialties,
  selectedSpecialties,
  isEditing,
}) => {
  const { t } = useTranslation();

  const {
    fields: providedServiceFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "providedServices" as never,
  });

  const clinicAlreadyAdded = providedServiceFields.length > 0;

  const addProvidedService = () => {
    append({ code: ProvidedServiceEnum.CLINIC, price: 0 } as never);
  };

  return (
    <>
      {/* Reference Number - Only for new doctors */}
      {!isEditing && (
        <Controller
          name="refNumber"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              required
              label={t("staff.manage.details.referenceNumber")}
              type="text"
              {...field}
              leftIcon={<User size={16} />}
              error={fieldState.error?.message}
            />
          )}
        />
      )}

      {/* Career Start Year */}
      <Controller
        name="careerStartYear"
        control={control}
        render={({ field, fieldState }) => (
          <Input
            required
            type="date"
            label={t("staff.manage.details.careerStartYear")}
            {...field}
            error={fieldState.error?.message}
          />
        )}
      />

      {/* Specialties */}
      <div className="col-span-2">
        <Controller
          name="specialties"
          control={control}
          render={({ field }) => (
            <SpecialtySearchAutocomplete
              specialties={specialties.map((specialty) => ({
                id: specialty.id as string,
                code: specialty.code,
                label: specialty.label,
              }))}
              placeholder={t(
                "facilitySettings.services.specialtiesPlaceholder",
              )}
              title={t("facilitySettings.services.medicalSpecialties")}
              label={t("facilitySettings.services.selectSpecialties")}
              getSelectedCountText={(count: number) =>
                t("facilitySettings.services.specialtiesSelected", { count })
              }
              selectedSpecialties={selectedSpecialties || []}
              showSelectedCount
              onSpecialtiesChange={(selected) => {
                const specialtyObjects = selected.map((s) => {
                  const fullSpecialty = specialties.find(
                    (sp) => String(sp.id) === String(s.id),
                  );
                  return {
                    id: fullSpecialty?.id,
                    code: fullSpecialty?.code || s.code,
                    label: fullSpecialty?.label || s.label,
                  } as Specialty;
                });
                field.onChange(specialtyObjects);
              }}
            />
          )}
        />
      </div>

      {/* Provided Services */}
      <div className="col-span-2">
        <label className="block mb-1 text-sm font-medium text-neutral-700">
          {t("staff.manage.details.selectServiceType")}
        </label>
        <div className="space-y-3">
          {providedServiceFields.map((field, index) => (
            <div key={field.id} className="flex gap-3 items-center">
              <div className="flex-1">
                <Controller
                  name={`providedServices.${index}.code` as const}
                  control={control}
                  render={({ field: codeField }) => (
                    <Select
                      options={[
                        {
                          value: ProvidedServiceEnum.CLINIC,
                          label: t("staff.manage.details.serviceTypes.clinic"),
                        },
                      ]}
                      value={codeField.value}
                      onChange={(value) => codeField.onChange(value)}
                      disabled
                    />
                  )}
                />
              </div>
              <div className="w-40">
                <Controller
                  name={`providedServices.${index}.price` as const}
                  control={control}
                  render={({ field: priceField }) => (
                    <Input
                      type="number"
                      rightAddon={t("staff.manage.details.currencyCode")}
                      placeholder="0"
                      value={priceField.value || 0}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        priceField.onChange(value);
                      }}
                      onBlur={priceField.onBlur}
                      name={priceField.name}
                    />
                  )}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(index)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="ghost"
            onClick={addProvidedService}
            disabled={clinicAlreadyAdded}
            className="w-full border-2 border-dashed border-neutral-300 hover:border-primary-400 hover:bg-ews-primary-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-neutral-300 disabled:hover:bg-transparent"
          >
            + {t("staff.manage.details.addService")}
          </Button>
        </div>
        <p className="mt-1 text-xs text-neutral-500">
          {t("staff.manage.details.addServiceDescription")}
        </p>
      </div>
    </>
  );
};
